import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchWallet24hProfitAndLoss,
  fetchWallet7dHistoricalPnL,
} from '#/api/apiExchange';
import { useWebsocketEventListener } from '#/components/hooks';
import { useSelector } from '#/reduxState/store';

import { minutes, seconds } from './utils';

const useInvalidatePnLQueries = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: ['walletHistoricalPnL24h'],
    });
  };
};

/**
 * We have to invalidate date on deposit/withdrawals to prevent displaying incorrect PnL.
 * Alternativelly we can use apply changed quantities.
 */
export const useGlobalPositionPnlQueriesInvalidator = () => {
  const invalidateQueries = useInvalidatePnLQueries();

  useWebsocketEventListener('deposits', () => {
    invalidateQueries();
  });
  useWebsocketEventListener('withdrawals', () => {
    invalidateQueries();
  });
};

export const useLast24hWalletPnLQuery = () => {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  return useQuery<{
    firstTotalPnL: number;
    lastTotalPnl: number;
    firstNonZeroEquity: number;
    lastEquity: number;
  }>({
    queryKey: ['walletHistoricalPnL24h', walletAddress],
    queryFn: !walletAddress
      ? skipToken
      : async () => fetchWallet24hProfitAndLoss(walletAddress),
    staleTime: minutes(3),
    refetchOnWindowFocus: false,
    refetchInterval: minutes(5),
  });
};

export const use7daysPortfolioPnlChartQuery = ({
  walletAddress,
  isVault,
}: {
  walletAddress: string;
  isVault: boolean;
}) => {
  return useQuery({
    queryKey: ['walletPortfolioPnLChart', walletAddress, isVault],
    queryFn: !walletAddress
      ? skipToken
      : () => fetchWallet7dHistoricalPnL(walletAddress, isVault),
    staleTime: seconds(30),
    refetchOnWindowFocus: false,
    refetchInterval: minutes(2),
  });
};
